import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  DropdownMenuItemType,
  PrimaryButton,
  Text,
  TextField
} from "@fluentui/react";
import { useState } from "react";

import {
  ACTION_REASONS,
  ACTIONS_REQUIRING_REASON,
  MIN_WORDS_REASON
} from "../constants";

import { PermissionsAction } from "@/common/types/permissions.types";

interface ActionConfirmDialogProps {
  isVisible: boolean;
  action: PermissionsAction;
  actionMessage: string;
  actionConfirmText?: string;
  isFetching: boolean;
  statusChangeReason: string;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onDismiss: () => void;
}

const REASON_LABELS: Record<string, string> = {
  reject: "Motivo da Rejeição",
  cancel: "Motivo do Cancelamento",
  "approve-commercially": "Motivo da Aprovação Comercial"
};

export function ActionConfirmDialog({
  isVisible,
  action,
  actionMessage,
  actionConfirmText,
  isFetching,
  statusChangeReason,
  onReasonChange,
  onConfirm,
  onDismiss
}: ActionConfirmDialogProps): JSX.Element {
  const [isCustomReason, setIsCustomReason] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>(undefined);

  const needsReason = ACTIONS_REQUIRING_REASON.includes(action);
  const reasons = ACTION_REASONS[action] || [];
  const reasonKeys = reasons
    .filter(reason => !reason.itemType)
    .map(reason => reason.key);

  const reasonWordCount = statusChangeReason
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  const isReasonValid = reasonWordCount >= MIN_WORDS_REASON;
  const reasonLabel = REASON_LABELS[action] || "Motivo";

  function handleDismiss() {
    onDismiss();
    setIsCustomReason(false);
    setSelectedKey(undefined);
  }

  function handleConfirm() {
    onConfirm();
    setIsCustomReason(false);
    setSelectedKey(undefined);
  }

  let dropdownSelectedKey: string = undefined;

  if (isCustomReason) {
    dropdownSelectedKey = "OTHER";
  } else if (reasonKeys.includes(selectedKey)) {
    dropdownSelectedKey = selectedKey;
  }

  return (
    <Dialog
      hidden={!isVisible}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Confirmar ação no contrato",
        closeButtonAriaLabel: "Fechar"
      }}
      modalProps={{ isBlocking: true, containerClassName: "dialog-container" }}
      minWidth={400}
      maxWidth={600}
    >
      {!needsReason && (
        <Text variant="mediumPlus">
          {actionMessage
            ? `Deseja ${actionMessage}?`
            : "Deseja realizar essa operação?"}
        </Text>
      )}

      {actionConfirmText && <Text variant="small">{actionConfirmText}</Text>}

      {needsReason && (
        <>
          {reasons.length > 0 && (
            <Dropdown
              label={reasonLabel}
              required
              placeholder="Selecione o motivo"
              selectedKey={dropdownSelectedKey}
              options={[
                ...reasons,
                {
                  key: "divider-other",
                  text: "-",
                  itemType: DropdownMenuItemType.Divider
                },
                { key: "OTHER", text: "Outro (personalizado)" }
              ]}
              onChange={(_, option) => {
                if (!option) return;

                if (option.key === "OTHER") {
                  setIsCustomReason(true);
                  setSelectedKey("OTHER");
                  onReasonChange("");
                } else {
                  setIsCustomReason(false);
                  setSelectedKey(String(option.key));
                  onReasonChange(option.text);
                }
              }}
              calloutProps={{ styles: { root: { width: "500px" } } }}
              styles={{
                dropdownItem: { height: "auto" },
                dropdownOptionText: {
                  overflow: "visible",
                  whiteSpace: "normal"
                },
                subComponentStyles: {
                  panel: {
                    main: {
                      selectors: {
                        "@media(max-width: 480px)": { width: "100vw" }
                      }
                    }
                  }
                }
              }}
            />
          )}

          {(isCustomReason || reasons.length === 0) && (
            <TextField
              label="Descreva o motivo"
              required
              multiline
              rows={3}
              value={statusChangeReason}
              onChange={(_, val) => onReasonChange(val ?? "")}
              placeholder="Explique brevemente a razão para esta ação"
              styles={{ root: { width: "100%", marginTop: 8 } }}
              errorMessage={
                reasonWordCount > 0 && reasonWordCount < MIN_WORDS_REASON
                  ? "Dê uma descrição mais detalhada sobre o ocorrido."
                  : undefined
              }
            />
          )}
        </>
      )}

      <DialogFooter>
        <DefaultButton text="Não" onClick={handleDismiss} />
        <PrimaryButton
          id="confirm-release-credit-button"
          onClick={handleConfirm}
          text="Sim"
          disabled={isFetching || (needsReason && !isReasonValid)}
        />
      </DialogFooter>
    </Dialog>
  );
}
